const STORAGE_KEY = 'op.guestReturnPath';

/**
 * Only claim pages a customer can legitimately be sent back to. Anything else is dropped rather
 * than sanitised — "/start?next=..." is a redirector the moment it accepts a path we did not write,
 * and a phisher would only need to get one arbitrary URL through it.
 */
const ALLOWED_PREFIXES = ['/view/', '/print/', '/sign/'];

const CONTROL_CHAR_MAX = 0x1f;
const DELETE_CHAR = 0x7f;

const storage = (): Storage | null => {
  if (typeof window === 'undefined') return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const hasControlCharacter = (value: string): boolean =>
  [...value].some(character => {
    const code = character.charCodeAt(0);

    return code <= CONTROL_CHAR_MAX || code === DELETE_CHAR;
  });

/**
 * A path is only safe if it is one of ours and cannot be read as an absolute URL. `//evil.com` is
 * protocol-relative and `/\evil.com` is treated as protocol-relative by some browsers, so both are
 * rejected before the prefix check rather than after it. Control characters go too: a newline is
 * enough to split a header or truncate the value somewhere downstream.
 */
export const isSafeReturnPath = (path: unknown): path is string => {
  if (typeof path !== 'string' || !path.startsWith('/')) return false;
  if (path.startsWith('//') || path.startsWith('/\\')) return false;
  if (hasControlCharacter(path)) return false;

  return ALLOWED_PREFIXES.some(prefix => path.startsWith(prefix));
};

export const setGuestReturnPath = (path: unknown): void => {
  if (!isSafeReturnPath(path)) return;

  storage()?.setItem(STORAGE_KEY, path);
};

/**
 * The claim a stored path points at, so the caller can check it against the order that was actually
 * looked up. Every allowed prefix carries a claim id in its second segment — `/view/` and `/print/`
 * name the route param `token`, but both pass it straight to `findClaimById`.
 */
export const claimIdFromReturnPath = (path: unknown): string | null => {
  if (!isSafeReturnPath(path)) return null;

  const [, , claimId] = path.split('?')[0].split('/');

  return claimId || null;
};

/** One-shot: reading consumes it, so a stale path cannot hijack a later, unrelated lookup. */
export const takeGuestReturnPath = (): string | null => {
  const path = storage()?.getItem(STORAGE_KEY) ?? null;
  storage()?.removeItem(STORAGE_KEY);

  return isSafeReturnPath(path) ? path : null;
};
